/* eslint-disable react-refresh/only-export-components -- toast store + imperative helpers co-located with Toaster */
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

type ToastTone = 'success' | 'error' | 'info';
/** One follow-up the toast offers (e.g. "Open" after accepting a request). Clicking dismisses. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}
interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  action?: ToastAction;
}

interface ToastState {
  toasts: Toast[];
  push: (tone: ToastTone, message: string, action?: ToastAction) => void;
  dismiss: (id: number) => void;
}

let seq = 1;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (tone, message, action) =>
    set((s) => ({ toasts: [...s.toasts, { id: seq++, tone, message, action }] })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Imperative helpers for use outside components (mutation callbacks). */
export const toast = {
  success: (m: string, action?: ToastAction) => useToastStore.getState().push('success', m, action),
  error: (m: string, action?: ToastAction) => useToastStore.getState().push('error', m, action),
  info: (m: string, action?: ToastAction) => useToastStore.getState().push('info', m, action),
};

/** Plain toasts self-dismiss after 5 s; one with an action gets longer (WCAG 2.2.1 — a keyboard
 *  or screen-reader user needs real time to reach the button), and either kind pauses while the
 *  pointer is over it or it holds focus, resuming with whatever time was left (final-review M2). */
const PLAIN_TIMEOUT_MS = 5000;
const ACTION_TIMEOUT_MS = 10_000;

function ToastItem({ toast: t }: { toast: Toast }) {
  const { t: translate } = useTranslation();
  const dismiss = useToastStore((s) => s.dismiss);
  const remainingRef = useRef(t.action ? ACTION_TIMEOUT_MS : PLAIN_TIMEOUT_MS);
  const startedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const start = () => {
      startedAtRef.current = Date.now();
      timerRef.current = setTimeout(() => dismiss(t.id), remainingRef.current);
    };
    start();
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per toast id, not per tick
  }, [t.id, dismiss]);

  const pause = () => {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
  };
  const resume = () => {
    if (timerRef.current !== null) return;
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(() => dismiss(t.id), remainingRef.current);
  };

  const Icon = t.tone === 'success' ? CheckCircle2 : AlertCircle;
  return (
    <div
      // Errors are announced assertively (role=alert / aria-live=assertive) so a failure isn't
      // swallowed if the screen reader is mid-utterance; success/info stay polite (role=status).
      role={t.tone === 'error' ? 'alert' : 'status'}
      aria-live={t.tone === 'error' ? 'assertive' : 'polite'}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      className={cn(
        'flex items-start gap-2 rounded-card border bg-surface-card p-3 text-sm shadow-card',
        t.tone === 'error' ? 'border-status-danger' : 'border-line-hairline',
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          'mt-0.5 size-4 shrink-0',
          t.tone === 'success' && 'text-status-success',
          t.tone === 'error' && 'text-status-danger',
          t.tone === 'info' && 'text-status-info',
        )}
      />
      <p className="flex-1 text-ink-primary">{t.message}</p>
      {t.action && (
        <button
          type="button"
          onClick={() => {
            t.action?.onClick();
            dismiss(t.id);
          }}
          className="shrink-0 rounded-control px-1 text-sm font-semibold text-brand-secondary hover:underline"
        >
          {t.action.label}
        </button>
      )}
      <button
        onClick={() => dismiss(t.id)}
        aria-label={translate('common.dismiss')}
        className="rounded-control p-0.5 text-ink-muted hover:text-ink-primary"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed bottom-4 end-4 z-[60] flex w-80 flex-col gap-2">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} />
        </div>
      ))}
    </div>
  );
}
